import React, { useEffect, useState } from 'react';
import Button from '@repo/ui-components/button/Button';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import SupportArticleFeedbackCheckboxList from './SupportArticleFeedbackCheckboxList';

interface SupportArticleFeedbackFormProps {
  articleId?: string;
  articleSlug?: string;
}

interface InputProps {
  articleId?: string;
  articleSlug?: string;
  test?: number;
  questions?: {
    id: string;
    question: string;
  }[];
  reaction?: number;
  feedback?: string;
}

interface ReactionProps {
  title?: string;
  textboxQuestion?: string;
  questions?: {
    id: string;
    question: string;
  }[];
}

function SupportArticleFeedbackForm(
  props: Readonly<SupportArticleFeedbackFormProps>
) {
  const { tt } = useUiComponentsInternationalContext();
  const [inputs, setInputs] = useState<InputProps>({
    articleId: props.articleId,
    articleSlug: props.articleSlug,
    test: -100,
    questions: [],
  });

  const [submitted, setSubmitted] = useState(false);
  const [reactionFace, setReactionFace] = useState({});
  const [reaction, setReaction] = useState<ReactionProps>({});

  const handleFaceClick = face => {
    let faceInt;
    setReactionFace(face);
    if (face === 'happy') {
      faceInt = 1;
    } else if (face === 'meh') {
      faceInt = 0;
    } else if (face === 'unhappy') {
      faceInt = -1;
    }
    setInputs({
      articleId: props.articleId,
      articleSlug: props.articleSlug,
      questions: [],
      reaction: faceInt,
    });
  };

  const handleChange = event => {
    const name = event.target.name;
    const value = event.target.value;

    const questions = inputs.questions;

    if (name.includes('question')) {
      const index = questions.indexOf(value);
      if (index === -1) {
        questions.push(value);
      } else {
        questions.splice(index, 1);
      }

      setInputs(values => ({ ...values, questions }));
    } else {
      setInputs(values => ({ ...values, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    await fetch(process.env.GATSBY_ARTICLE_FEEDBACK_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(inputs),
    })
      .catch(() => alert('There was an error submitting your feedback'))
      .then(() => setSubmitted(true));
  };

  const buttonStatus = () => {
    return inputs.hasOwnProperty('questions') && inputs.questions.length > 0;
  };

  useEffect(() => {
    const happy = {
      questions: [
        {
          id: 'resolvedMyIssue',
          question: tt(s => s.supportArticleComponent.resolvedMyIssue),
        },
        {
          id: 'easyToUnderstand',
          question: tt(s => s.supportArticleComponent.easyToUnderstand),
        },
        {
          id: 'learnedSomethingNew',
          question: tt(s => s.supportArticleComponent.learnedSomethingNew),
        },
        {
          id: 'somethingElse',
          question: tt(s => s.supportArticleComponent.somethingElse),
        },
      ],
      title: tt(s => s.supportArticleComponent.whatYouLikedAboutContent),
      textboxQuestion: tt(
        s => s.supportArticleComponent.tellMoreAboutYourExperience
      ),
      reaction: 'happy',
    };
    const meh = {
      questions: [
        {
          id: 'somewhatResolved',
          question: tt(s => s.supportArticleComponent.somewhatResolved),
        },
        {
          id: 'showsProductNotInformation',
          question: tt(
            s => s.supportArticleComponent.showsProductNotInformation
          ),
        },
        {
          id: 'somethingElse',
          question: tt(s => s.supportArticleComponent.somethingElse),
        },
        {
          id: 'onlyCoversSome',
          question: tt(s => s.supportArticleComponent.onlyCoversSome),
        },
        {
          id: 'differentResult',
          question: tt(s => s.supportArticleComponent.differentResult),
        },
      ],
      title: tt(s => s.supportArticleComponent.whatToDoBetter),
      textboxQuestion: tt(
        s => s.supportArticleComponent.tellMoreAboutYourExperience
      ),
      reaction: 'meh',
    };
    const unhappy = {
      questions: [
        {
          id: 'productNotShown',
          question: tt(s => s.supportArticleComponent.productNotShown),
        },
        {
          id: 'notListed',
          question: tt(s => s.supportArticleComponent.notListed),
        },
        {
          id: 'cantOpenLink',
          question: tt(s => s.supportArticleComponent.cantOpenLink),
        },
        {
          id: 'didNotResolveMyIssue',
          question: tt(s => s.supportArticleComponent.didNotResolveMyIssue),
        },
        {
          id: 'hardToUnderstand',
          question: tt(s => s.supportArticleComponent.hardToUnderstand),
        },
        {
          id: 'somethingElse',
          question: tt(s => s.supportArticleComponent.somethingElse),
        },
      ],
      title: tt(s => s.supportArticleComponent.tellUsMoreToGetItRightNextTime),
      textboxQuestion: tt(s => s.supportArticleComponent.anythingMoreToShare),
      reaction: 'unhappy',
    };

    if (reactionFace === 'happy') {
      setReaction(happy);
    } else if (reactionFace === 'meh') {
      setReaction(meh);
    } else if (reactionFace === 'unhappy') {
      setReaction(unhappy);
    }
  }, [reactionFace]);

  return (
    <div>
      {submitted ? (
        <div id="success-message" style={{ textAlign: 'center' }}>
          <i
            className="bi bi bi-check-circle-fill"
            style={{ fontSize: '3rem', color: 'green' }}
          ></i>
          <p>
            {tt(s => s.supportArticleComponent.weAppreciateYourReplyMessage)}
          </p>
        </div>
      ) : (
        <div style={{ width: '70%' }}>
          <div>
            <h5>
              {tt(
                s => s.supportArticleComponent.wouldLoveFeedbackOnPageYouRead
              )}
            </h5>
            <button
              name="happy"
              onClick={() => handleFaceClick('happy')}
              style={{ border: 'none', background: 'white' }}
            >
              <i
                className="bi bi-emoji-smile-fill"
                style={{ fontSize: '3rem', color: 'green' }}
              />
            </button>
            <button
              name="meh"
              onClick={() => handleFaceClick('meh')}
              style={{ border: 'none', background: 'white' }}
            >
              <i
                className="bi bi-emoji-neutral-fill"
                style={{ fontSize: '3rem', color: '#c8c800' }}
              ></i>
            </button>
            <button
              name="unhappy"
              onClick={() => handleFaceClick('unhappy')}
              style={{ border: 'none', background: 'white' }}
            >
              <i
                className="bi bi-emoji-frown-fill"
                style={{ fontSize: '3rem', color: 'red' }}
              ></i>
            </button>
          </div>

          {reaction.hasOwnProperty('questions') ? (
            <>
              <p>{reaction.title}</p>
              <div style={{ paddingTop: '5px' }}>
                <SupportArticleFeedbackCheckboxList
                  questions={reaction.questions}
                  handleChange={handleChange}
                />
              </div>

              <label>
                <p>{reaction.textboxQuestion}</p>
                <textarea
                  name="feedback"
                  rows={4}
                  cols={40}
                  value={inputs.feedback || ''}
                  onChange={handleChange}
                />
              </label>
              <div style={{ paddingTop: '20px' }}>
                <p>
                  <Button
                    name="submit"
                    onClick={handleSubmit}
                    disabled={!buttonStatus()}
                    importance="primary"
                  >
                    {tt(s => s.supportArticleComponent.submitButton)}
                  </Button>
                </p>
              </div>
            </>
          ) : (
            ''
          )}
        </div>
      )}
    </div>
  );
}

export default SupportArticleFeedbackForm;
